// Syntax highlighting for the reference's code blocks.
//
// highlight.js core with the grammars registered by hand, not the bundled
// build: the API docs emit shell, PowerShell (the Windows setup lines), Python
// and JSON and nothing else, and the full package carries ~190 languages the
// product would ship and never render.
//
// The colours are NOT highlight.js's. Its stylesheets hardcode hex, which this
// codebase does not do and which cannot flip between themes; the `hljs-*`
// classes are styled from the `--code-*` design tokens instead (theme.css).

import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';
import powershell from 'highlight.js/lib/languages/powershell';
import python from 'highlight.js/lib/languages/python';

/** The languages the reference can render. A fence naming anything else, or a
 *  block with no language at all, renders as plain text - which is correct,
 *  not a fallback: guessing a grammar mis-colours more often than it helps. */
export type CodeLang = 'bash' | 'powershell' | 'python' | 'json';

const GRAMMARS = { bash, powershell, python, json } as const;

for (const [name, grammar] of Object.entries(GRAMMARS)) {
  hljs.registerLanguage(name, grammar);
}

// The aliases a fence or a media type actually arrives as. `sh`/`shell`/`curl`
// all mean the shell snippet; `application/json` is how a response's media
// type names itself.
const ALIASES: Record<string, CodeLang> = {
  bash: 'bash',
  sh: 'bash',
  shell: 'bash',
  console: 'bash',
  curl: 'bash',
  powershell: 'powershell',
  ps1: 'powershell',
  pwsh: 'powershell',
  python: 'python',
  py: 'python',
  json: 'json',
  'application/json': 'json',
};

/** resolveLang maps a fence info string or media type to a registered grammar,
 *  or undefined when nothing here can highlight it. */
export function resolveLang(hint: string | undefined): CodeLang | undefined {
  if (!hint) return undefined;
  return ALIASES[hint.trim().toLowerCase()];
}

/** highlightToHTML returns highlight.js markup for `text`, or null when the
 *  language is unknown or the grammar throws - the caller then renders the
 *  text verbatim.
 *
 *  The returned string is safe to inject: highlight.js escapes `&`, `<` and
 *  `>` in the source it wraps, so the only markup in the result is its own
 *  `<span class="hljs-…">`. It is never given anything but generated snippets
 *  and specification text either way. */
export function highlightToHTML(text: string, lang: CodeLang | undefined): string | null {
  if (!lang) return null;
  try {
    return hljs.highlight(text, { language: lang, ignoreIllegals: true }).value;
  } catch {
    // A grammar that throws on an odd snippet must not take the page with it;
    // the block still renders, just uncoloured.
    return null;
  }
}
